<template>
  <page-layout :title="'Graduation category detail'">
    <div class="md-layout">
      <div class="md-layout-item">
        <md-card>
          <md-card-header class="md-card-header-text md-card-header-green">
            <div class="card-text">
              <h4 class="title text-lg">
                {{ $t("graduation_category.graduation_info") }}
              </h4>
            </div>
          </md-card-header>

          <md-card-content>
            <validation-form @on-submit="editGraduationCategory">
              <div class="md-layout mt-15">
                <div class="md-layout-item md-size-33 md-small-size-100">
                  <validation-field
                    :validations="{ required: true }"
                    name="name"
                  >
                    <label>{{ $t("common.name") }}</label>
                    <md-input required v-model="formData.name"></md-input>
                    <span class="md-error" v-if="serverValidations.name">{{
                      serverValidations.name
                    }}</span>
                  </validation-field>
                </div>

                <div class="md-layout-item md-size-33 md-small-size-100">
                  <md-field>
                    <label>{{ $t("common.status") }}</label>
                    <md-select
                      :disabled="isReadOnly"
                      name="status"
                      v-model="formData.status"
                      id="status"
                    >
                      <md-option name="Active" value="Active"
                        >{{ $t("common.active") }}
                      </md-option>
                      <md-option name="Inactive" value="Inactive"
                        >{{ $t("common.inactive") }}
                      </md-option>
                    </md-select>
                  </md-field>
                </div>

                <div class="md-layout-item md-size-33 md-small-size-100">
                  <md-field>
                    <label>{{ $t("common.description") }}</label>
                    <md-input
                      :readonly="isReadOnly"
                      v-model="formData.description"
                    ></md-input>
                  </md-field>
                </div>

                <div class="md-layout-item md-size-33 md-small-size-100">
                  <validation-field
                    :validations="{ required: true }"
                    class="validate-multi-select"
                    name="subject"
                    type="multi-select"
                  >
                    <template #default="{ errors }">
                      <label class="subject-label"
                        >{{ $t("common.subject") }}
                        <span style="color:red">*</span></label
                      >
                      <checkbox-multi-select
                        :isDisabled="isReadOnly"
                        :options="subjectOpts"
                        @filtering="filterSubject($event)"
                        v-model="formData.subject_ids"
                        :style="{ marginTop: '-6px' }"
                      />
                      <span class="multi-select-error">{{ errors[0] }}</span>
                    </template>
                  </validation-field>
                </div>
              </div>

              <!-- LIST  -->
              <div class="md-layout mt-10 mb-10">
                <!-- list subjects -->
                <div
                  class="md-layout-item md-size-50 md-small-size-100 subject-list"
                >
                  <p class="list-program">
                    {{
                      $t("graduation_category.text_graduation_include_subject")
                    }}
                  </p>
                  <ol v-for="(item, index) in subjectSelected" :key="index">
                    <li class="text-base mt-2">
                      {{ index + 1 }}. {{ item.name }}
                    </li>
                  </ol>
                </div>

                <!-- list programs -->
                <div
                  class="md-layout-item md-size-50 md-small-size-100 form-list"
                >
                  <p class="list-program">
                    {{ $t("graduation_category.list_of_program_include") }}
                    {{ formData.name }} ({{ programs && programs.length }})
                  </p>
                  <div v-if="programs">
                    <ol v-for="(item, index) in programs" :key="index">
                      <li class="text-base mt-2">
                        {{ index + 1 }}. {{ item.name }}
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <!-- form submit -->
              <div class="from-submit">
                <md-button class="md-raised mr-5" @click="goToPreviousPage"
                  >{{ $t("common.cancel") }}
                </md-button>
                <md-button v-if="!isReadOnly" type="submit" class="md-success"
                  >{{ $t("common.update") }}
                </md-button>
              </div>
            </validation-form>
          </md-card-content>
        </md-card>
      </div>
    </div>
  </page-layout>
</template>

<script>
import { service_paths } from "@/constants/Services";
import { genErrMsgOj, hasHistory, Message } from "@/helpers/utils";
import PageLayout from "../PageLayout.vue";
import CheckboxMultiSelect from "@/components/Base/CheckboxMultiSelect.vue";
import { graduation_category_router_names } from "@/routes/routes";
import { GraduationCategory } from "@/constants/Permissions";
import debounce from "lodash/debounce";

export default {
  components: { PageLayout, CheckboxMultiSelect },
  data() {
    return {
      GraduationCategory,
      programs: null,
      serverValidations: {},
      formData: {
        name: null,
        status: null,
        description: null,
        subject_ids: []
      },
      subjectOpts: [],
      subjectSelected: []
    };
  },
  computed: {
    isReadOnly() {
      return !this.$can(
        GraduationCategory.actions.edit,
        GraduationCategory.subject
      );
    },
    routeId() {
      return this.$route.params.id;
    }
  },
  watch: {
    "formData.name"() {
      this.serverValidations.name && delete this.serverValidations.name;
    },
    "formData.subject_ids"(newSubject) {
      this.fetchSubjectOptions();
      this.subjectSelected = this.subjectOpts.filter(item =>
        newSubject.includes(item.id)
      );
    }
  },
  created() {
    this.fetchSubjectOptions("", "created");
  },
  methods: {
    async fetchGraduationCategory() {
      const { data } = await this.$axios.get(
        `${service_paths.graduation_categories}/${this.routeId}`
      );
      this.programs = data?.programs;
      this.formData.name = data?.name;
      this.formData.status = data?.status;
      this.formData.description = data?.description;
      this.formData.subject_ids = data?.subjects?.map(item => item.id);
    },

    filterSubject(event) {
      this.debounceSubject(event.text);
    },

    debounceSubject: debounce(function(searchKey) {
      this.fetchSubjectOptions(searchKey);
    }, 1000),

    fetchSubjectOptions(searchKey = "", type = "search") {
      this.$axios
        .get(`${service_paths.subjects}`, {
          status: "Active",
          "subject_display_name__~": searchKey,
          limit: 10000
        })
        .then(({ data }) => {
          if (type === "created") this.fetchGraduationCategory();

          let newOptions = data.data.filter(
            item => !this.formData.subject_ids.includes(item.id)
          );
          newOptions.map(subject => {
            subject.name = subject.subject_display_name || subject.name;
          });
          this.subjectOpts = [...this.subjectSelected, ...newOptions];
        });
    },

    editGraduationCategory() {
      this.$axios
        .put(
          `${service_paths.graduation_categories}/${this.routeId}`,
          this.formData
        )
        .then(() => {
          Message.success(this.$t("common.edit_success"));
          this.$router.push({
            name: graduation_category_router_names.listPage
          });
        })
        .catch(error => {
          const errData = error.response?.data;
          if (errData) {
            this.serverValidations = genErrMsgOj(errData.messages);
          }
        });
    },

    goToPreviousPage() {
      hasHistory() ? this.$router.go(-1) : this.$router.push("/");
    }
  }
};
</script>

<style lang="scss" scoped>
.form-actions {
  padding: 30px 0;
  text-align: center;

  & > * ~ * {
    margin-left: 15px;
  }

  .md-button {
    box-shadow: 0px 2px 4px 0px #99999924;
    border-radius: 100px;
  }
}

.list-program {
  font-size: 18px;
  font-weight: 400;
  overflow-wrap: break-word;
}

.from-submit {
  display: flex;
  justify-content: center;
}

.md-error {
  top: 40px !important;
}

li {
  overflow-wrap: break-word;
}

.required-subject {
  color: #ff1744;
  font-size: 12px;
}

.subject-label {
  display: block;
  margin-top: 3px;
  margin-bottom: -3px;
  font-size: 11px;
  color: #aaa;
  font-weight: 400;
}
</style>
